import React, { useEffect } from 'react';
import { Trash2, AlertTriangle, AlertCircle, Info, CheckCircle2, X } from 'lucide-react';

const VARIANT_CONFIG = {
  danger: {
    icon: Trash2,
    bg: '#fef2f2',
    border: '#fee2e2',
    color: '#ef4444',
    buttonGradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    buttonShadow: '0 4px 14px rgba(239, 68, 68, 0.3)',
    defaultConfirmText: 'Sim, excluir'
  },
  warning: {
    icon: AlertTriangle,
    bg: '#fffbeb',
    border: '#fef3c7',
    color: '#f59e0b',
    buttonGradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    buttonShadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
    defaultConfirmText: 'Continuar'
  },
  info: {
    icon: Info,
    bg: '#eff6ff',
    border: '#dbeafe',
    color: '#3b82f6',
    buttonGradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    buttonShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
    defaultConfirmText: 'Confirmar'
  },
  success: {
    icon: CheckCircle2,
    bg: '#f0fdf4',
    border: '#dcfce7',
    color: '#10b981',
    buttonGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    buttonShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
    defaultConfirmText: 'Concluir'
  },
  shopee: {
    icon: AlertCircle,
    bg: '#fff5f2',
    border: '#fed7cf',
    color: '#ee4d2d',
    buttonGradient: 'linear-gradient(135deg, #ee4d2d 0%, #ff5722 100%)',
    buttonShadow: '0 4px 14px rgba(238, 77, 45, 0.3)',
    defaultConfirmText: 'Confirmar'
  }
};

const ConfirmModal = ({
  isOpen,
  title = 'Confirmação',
  message = 'Tem certeza que deseja prosseguir?',
  detail = null,
  confirmText,
  cancelText = 'Cancelar',
  type = 'danger',
  isAlert = false,
  onConfirm,
  onCancel
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel && onCancel();
      } else if (e.key === 'Enter' && !isAlert) {
        // Option to confirm on Enter
        onConfirm && onConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, onConfirm, isAlert]);

  if (!isOpen) return null;

  const config = VARIANT_CONFIG[type] || VARIANT_CONFIG.danger;
  const IconComponent = config.icon;
  const finalConfirmText = confirmText || (isAlert ? 'Entendi' : config.defaultConfirmText);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel && onCancel();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 99999,
        animation: 'confirmOverlayFadeIn 0.2s ease-out forwards'
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '460px',
          padding: '24px',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          animation: 'confirmModalPopIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          fontFamily: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        {/* Botão Fechar (X) */}
        <button
          onClick={onCancel}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            background: 'transparent',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#334155';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#94a3b8';
          }}
          title="Fechar"
        >
          <X size={18} />
        </button>

        {/* Cabeçalho do Modal com Ícone e Título */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              minWidth: '48px',
              borderRadius: '14px',
              backgroundColor: config.bg,
              border: `1px solid ${config.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: config.color
            }}
          >
            <IconComponent size={24} />
          </div>

          <div style={{ flex: 1, paddingRight: '20px' }}>
            <h3
              style={{
                margin: '2px 0 6px 0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#0f172a',
                letterSpacing: '-0.02em',
                lineHeight: 1.3
              }}
            >
              {title}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: '14px',
                color: '#64748b',
                lineHeight: '1.55',
                whiteSpace: 'pre-line'
              }}
            >
              {message}
            </p>
          </div>
        </div>

        {/* Detalhe extra opcional (ex: nome do item, código ou aviso secundário) */}
        {detail && (
          <div
            style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '12px 14px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#334155',
              lineHeight: 1.5
            }}
          >
            {detail}
          </div>
        )}

        {/* Botões de Ação */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '22px'
          }}
        >
          {!isAlert && (
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 18px',
                borderRadius: '11px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
                e.currentTarget.style.borderColor = '#94a3b8';
                e.currentTarget.style.color = '#1e293b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.color = '#475569';
              }}
            >
              {cancelText}
            </button>
          )}

          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '10px 22px',
              borderRadius: '11px',
              border: 'none',
              background: config.buttonGradient,
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: config.buttonShadow,
              transition: 'all 0.18s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.filter = 'brightness(1.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.filter = 'none';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.98)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
          >
            {finalConfirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes confirmOverlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes confirmModalPopIn {
          0% {
            opacity: 0;
            transform: scale(0.93) translateY(12px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ConfirmModal;
